import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { getPaymentMethodsPayload } from '@features/invoicingSettings/modules/paymentsAndReminders'
import { ZervantSettings } from '@features/invoicingSettings/types/zervantSettings'
import { getCustomTemplateSettingsPayload } from '@features/invoicingSettings/utils/getCustomTemplateSettingsPayload'
import { getInvoicingSettingsPreviewPayload } from '@features/invoicingSettings/utils/getInvoicingSettingsPreviewPayload'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { InvoiceSettingsForm } from '../../../types/invoiceSettingsForm'
import { getCompanyPayload } from '../../../utils/invoiceSettingsPayload/getCompanyPayload'
import { getModelsPayload } from '../../../utils/invoiceSettingsPayload/getModelsPayload'

export const useInvoiceSettingsPreviewPayload = () => {
  const { t } = useTranslation()
  const { organization, isLoading: isOrganizationLoading } = useUserOrganization()
  const { data: zervantSettings, isLoading: isZervantSettingsLoading } = useZervantSettings()
  const { getDocumentColumns } = useGetZervantDocumentSettings(InvoiceDocumentType.Invoice)
  const isLoading = isOrganizationLoading || isZervantSettingsLoading

  // Only show VAT column if organization is not VAT exempted
  const visibleColumns = useMemo(() => {
    let columns = getDocumentColumns()

    if (organization?.isVatExempted) {
      columns = columns.filter((column) => column !== 'vatPct')
    }

    return columns
  }, [getDocumentColumns, organization?.isVatExempted])

  const getInvoiceSettingsPreviewPayload = useCallback(
    (formValues: InvoiceSettingsForm) => {
      const now = new Date()

      const settingsValues: ZervantSettings | undefined = zervantSettings
        ? {
            ...zervantSettings,
            ...(zervantSettings.models && {
              models: getModelsPayload(zervantSettings.models, formValues),
            }),
            values: {
              ...zervantSettings.values,
              company: getCompanyPayload(zervantSettings.values.company, formValues),
              paymentMethods: getPaymentMethodsPayload(
                zervantSettings.values.paymentMethods,
                formValues.paymentMethods,
              ),
            },
          }
        : undefined

      return getInvoicingSettingsPreviewPayload({
        customTemplateSettings: getCustomTemplateSettingsPayload(formValues),
        documentDate: now,
        organization,
        previewValues: {
          currency: formValues.currency,
          footerNote: formValues.footerNote,
          language: formValues.language,
          message: formValues.message,
          paymentTerm: formValues.paymentTerm,
          productType: formValues.productType,
          vatRateId: formValues.vatRateId,
          vatRatePercent: formValues.vatRatePercent,
        },
        t,
        visibleColumns,
        zervantSettings: settingsValues,
      })
    },
    [organization, t, visibleColumns, zervantSettings],
  )

  return {
    getInvoiceSettingsPreviewPayload,
    isLoading,
    zervantSettings,
  }
}
